import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { LineChart as LineIcon, BarChart3, TrendingUp, FileDown, Database } from "lucide-react";
import { formatBig } from "@/pages/Overview";
import ChartLegend, { useToggleableSeries } from "@/components/ChartLegend";
import ChartDateRange, { useChartDateRange } from "@/components/ChartDateRange";

const platformLabels = {
  meta: "Meta", facebook: "Facebook", instagram: "Instagram",
  google_ads: "Google Ads", youtube: "YouTube", x_twitter: "X (Twitter)",
  linkedin: "LinkedIn", pinterest: "Pinterest", tiktok: "TikTok", awstats: "AWStats",
  google_analytics: "Google Analytics", google_search_console: "Google Search Console",
  bing_ads: "Bing Ads", bing_analytics: "Bing Analytics",
};

const COLORS = ["#002FA7", "#10B981", "#FFC107", "#E53935", "#0EA5E9", "#A855F7", "#F97316", "#14B8A6"];

// Shift a "YYYY-MM" string by N months. -12 = previous year.
function shiftMonth(ym, deltaMonths) {
  if (!ym) return ym;
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + deltaMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Charts() {
  const { selected } = useClients();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${selected.client_id}/dashboard`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selected?.client_id]);

  const platforms = useMemo(
    () => Object.keys(data?.by_platform || {}).filter((p) => p !== "worklogs"),
    [data],
  );

  if (!selected) return null;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="charts-page-loading">
        <Header />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={`skeleton-card-${i}`} className="rounded-sm border-zinc-200">
              <CardContent className="p-5 animate-pulse">
                <div className="h-3 w-20 bg-zinc-100 rounded-sm" />
                <div className="h-7 w-24 bg-zinc-100 rounded-sm mt-3" />
                <div className="h-2 w-32 bg-zinc-100 rounded-sm mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-6 animate-pulse">
            <div className="h-64 bg-zinc-50 rounded-sm" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (platforms.length === 0) {
    return (
      <div className="space-y-6" data-testid="charts-page-empty">
        <Header />
        <Card className="rounded-sm border-dashed border-zinc-300 bg-zinc-50/40">
          <CardContent className="p-12 text-center">
            <div className="mx-auto h-14 w-14 rounded-sm bg-zinc-900 text-white grid place-items-center mb-4">
              <BarChart3 className="h-7 w-7" />
            </div>
            <h3 className="font-display font-bold text-xl">No charts yet</h3>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
              Upload at least one CSV from any platform (Meta, Google Ads, AWStats, etc.)
              to see monthly trends, KPI tiles, and side-by-side comparisons here.
            </p>
            <a
              href={`/clients/${selected.client_id}/uploads`}
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 bg-[#002FA7] text-white hover:bg-[#00227A] rounded-sm text-sm transition-colors"
              data-testid="charts-empty-cta"
            >
              <FileDown className="h-4 w-4" /> Upload CSV data
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="charts-page">
      <Header platformCount={platforms.length} monthCount={data?.months?.length || 0} />
      <Tabs defaultValue={platforms[0]}>
        <TabsList className="flex flex-wrap gap-1 rounded-sm bg-zinc-100 p-1 h-auto">
          {platforms.map((p) => {
            const rows = data.by_platform[p] || [];
            return (
              <TabsTrigger key={p} value={p}
                className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#002FA7]"
                data-testid={`charts-tab-${p}`}>
                <span>{platformLabels[p] || p}</span>
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-mono px-1 rounded-sm bg-zinc-200/70 text-zinc-700">
                  {rows.length}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {platforms.map((p) => (
          <TabsContent key={p} value={p} className="space-y-6 mt-6">
            <PlatformChartTab platformKey={p} months={data.by_platform[p] || []} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function PlatformChartTab({ platformKey, months }) {
  const dateRange = useChartDateRange("last12");
  const lineToggle = useToggleableSeries();
  const barToggle = useToggleableSeries();
  // YoY toggle: when on, every metric is paired with a dashed previous-year
  // companion line + a ghost bar, and KPI tiles show a ▲/▼ delta.
  const [compareYoY, setCompareYoY] = useState(false);

  const metrics = useMemo(() => {
    const metricSet = new Set();
    months.forEach((m) => Object.keys(m.totals || {}).forEach((k) => metricSet.add(k)));
    return [...metricSet].slice(0, 6);
  }, [months]);
  const barMetrics = metrics.slice(0, 3);

  // Aggregate by period_month — different properties for the same platform can
  // produce multiple rows per month (e.g. 3 AWStats uploads in July). The chart
  // must show ONE x-axis point per calendar month with each metric summed.
  const series = useMemo(() => {
    const byMonth = new Map();
    months.forEach((m) => {
      const ym = m.period_month;
      if (!ym) return;
      let row = byMonth.get(ym);
      if (!row) {
        row = { month: ym };
        metrics.forEach((k) => { row[k] = 0; });
        byMonth.set(ym, row);
      }
      metrics.forEach((k) => { row[k] += Number(m.totals?.[k] || 0); });
    });
    return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  }, [months, metrics]);

  const visibleSeries = useMemo(() => dateRange.filter(series), [dateRange, series]);

  // Indexed lookup for the YoY companion data — every visible month is
  // matched against its same-month-previous-year row.
  const seriesByMonth = useMemo(() => {
    const m = new Map();
    series.forEach((r) => m.set(r.month, r));
    return m;
  }, [series]);

  // Enriched series carries `${metric}_prev` fields so a single chart can plot
  // both the current and YoY lines/bars from the same data array.
  const enrichedSeries = useMemo(() => {
    if (!compareYoY) return visibleSeries;
    return visibleSeries.map((r) => {
      const prevYm = shiftMonth(r.month, -12);
      const prevRow = seriesByMonth.get(prevYm) || {};
      const out = { ...r, prevMonth: prevYm };
      metrics.forEach((k) => { out[`${k}_prev`] = Number(prevRow[k] || 0); });
      return out;
    });
  }, [visibleSeries, compareYoY, seriesByMonth, metrics]);

  const actualRange = useMemo(() => {
    const sorted = series.map((r) => r.month).filter(Boolean).slice().sort();
    return { startYM: sorted[0], endYM: sorted[sorted.length - 1] };
  }, [series]);

  // KPI tile values are now scoped to the SELECTED date range so they stay in
  // sync with whatever the user is looking at on the charts below.
  const kpiTiles = useMemo(() => {
    return metrics.slice(0, 4).map((k, idx) => {
      const vals = visibleSeries.map((r) => Number(r[k] || 0));
      const total = vals.reduce((s, v) => s + v, 0);
      const latest = vals.length ? vals[vals.length - 1] : 0;
      const avg = vals.length ? total / vals.length : 0;
      // Trend = compare last to mean of previous; simple direction indicator.
      let trend = "flat";
      if (vals.length >= 2) {
        const prev = vals.slice(0, -1);
        const prevAvg = prev.reduce((s, v) => s + v, 0) / Math.max(prev.length, 1);
        if (latest > prevAvg * 1.05) trend = "up";
        else if (latest < prevAvg * 0.95) trend = "down";
      }
      // YoY: sum the previous-year equivalent of every visible month.
      let yoyTotal = 0;
      let yoyHasAny = false;
      visibleSeries.forEach((r) => {
        const prevRow = seriesByMonth.get(shiftMonth(r.month, -12));
        if (prevRow) {
          yoyTotal += Number(prevRow[k] || 0);
          yoyHasAny = true;
        }
      });
      const yoyDelta = (compareYoY && yoyHasAny && yoyTotal > 0)
        ? ((total - yoyTotal) / yoyTotal) * 100
        : null;
      return { metric: k, idx, total, latest, avg, trend, points: vals.length,
               yoyTotal, yoyDelta };
    });
  }, [metrics, visibleSeries, compareYoY, seriesByMonth]);

  const hasPointsInRange = visibleSeries.length > 0;

  return (
    <>
      {/* Tab-level master date range — applies to BOTH the line + bar chart below */}
      <Card className="rounded-sm border-zinc-200 bg-zinc-50">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 grid place-items-center rounded-sm bg-zinc-900 text-white">
              <TrendingUp className="h-4 w-4" />
            </div>
            <div>
              <div className="label-overline">{platformLabels[platformKey] || platformKey}</div>
              <div className="text-xs text-zinc-600 mt-0.5">
                Default view shows the last 12 months. Use the picker to widen or narrow.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-600 cursor-pointer select-none"
              data-testid={`charts-yoy-toggle-${platformKey}`}>
              <input type="checkbox"
                checked={compareYoY}
                onChange={(e) => setCompareYoY(e.target.checked)}
                className="h-3.5 w-3.5 rounded-sm border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]" />
              Compare YoY
            </label>
            <ChartDateRange
              {...dateRange}
              actualRange={actualRange}
              testIdPrefix={`charts-tab-range-${platformKey}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* KPI tiles */}
      {metrics.length === 0 ? (
        <Card className="rounded-sm border-dashed border-zinc-300">
          <CardContent className="p-8 text-center text-sm text-zinc-500">
            No numeric metrics detected for {platformLabels[platformKey] || platformKey}.
            CSV columns might be text-only.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {kpiTiles.map((tile) => (
            <Card
              key={tile.metric}
              className="rounded-sm border-zinc-200 overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-transform"
              data-testid={`charts-kpi-${platformKey}-${tile.metric}`}
            >
              <div className="h-1" style={{ backgroundColor: COLORS[tile.idx % COLORS.length] }} />
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="label-overline truncate" title={tile.metric}>{tile.metric}</div>
                  <TrendBadge dir={tile.trend} />
                </div>
                <div className="font-display font-extrabold text-2xl mt-2 tracking-tight">
                  {formatBig(tile.total)}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 font-mono">
                  total · {tile.points} mo
                </div>
                {compareYoY && tile.yoyDelta !== null && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded-sm bg-zinc-50"
                    data-testid={`charts-yoy-delta-${platformKey}-${tile.metric}`}>
                    <span className={tile.yoyDelta >= 0 ? "text-emerald-600" : "text-red-600"}>
                      {tile.yoyDelta >= 0 ? "▲" : "▼"} {Math.abs(tile.yoyDelta).toFixed(1)}%
                    </span>
                    <span className="text-zinc-400">vs prev yr ({formatBig(tile.yoyTotal)})</span>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-zinc-100 grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <div className="label-overline">Latest</div>
                    <div className="font-mono mt-0.5 text-zinc-900">{formatBig(tile.latest)}</div>
                  </div>
                  <div>
                    <div className="label-overline">Avg / mo</div>
                    <div className="font-mono mt-0.5 text-zinc-900">{formatBig(tile.avg)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {metrics.length > 0 && (
        <Card className="rounded-sm border-zinc-200" data-testid={`charts-line-${platformKey}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className="h-7 w-7 grid place-items-center rounded-sm bg-[#002FA7]/10 text-[#002FA7]">
                  <LineIcon className="h-3.5 w-3.5" />
                </div>
                <div>
                  <div className="label-overline">Monthly trend</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {hasPointsInRange
                      ? `${visibleSeries.length} data points in range — tap a chip to toggle a series`
                      : "No data points in the selected range"}
                  </div>
                </div>
              </div>
            </div>
            <ChartLegend
              keys={metrics}
              colors={COLORS}
              hidden={lineToggle.hidden}
              onToggle={lineToggle.toggle}
              testIdPrefix={`chart-line-${platformKey}`}
            />
            {hasPointsInRange ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={enrichedSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
                  <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatBig} width={50} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7" }} />
                  {metrics.map((k, idx) => (
                    <Line key={k} type="monotone" dataKey={k}
                          stroke={COLORS[idx % COLORS.length]} strokeWidth={2}
                          dot={{ r: 3 }} activeDot={{ r: 5 }}
                          hide={lineToggle.isHidden(k)} />
                  ))}
                  {compareYoY && metrics.map((k, idx) => (
                    <Line key={`${k}_prev`} type="monotone" dataKey={`${k}_prev`}
                          name={`${k} (prev yr)`}
                          stroke={COLORS[idx % COLORS.length]} strokeWidth={1.5}
                          strokeDasharray="5 4" opacity={0.55}
                          dot={false} activeDot={{ r: 4 }}
                          hide={lineToggle.isHidden(k)} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState
                msg="No data in this range. Widen the date range or upload data for the selected months."
              />
            )}
          </CardContent>
        </Card>
      )}

      {metrics.length > 0 && (
        <Card className="rounded-sm border-zinc-200" data-testid={`charts-bar-${platformKey}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className="h-7 w-7 grid place-items-center rounded-sm bg-emerald-50 text-emerald-700">
                  <BarChart3 className="h-3.5 w-3.5" />
                </div>
                <div>
                  <div className="label-overline">Monthly comparison</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Top {barMetrics.length} metrics, month-over-month
                  </div>
                </div>
              </div>
            </div>
            <ChartLegend
              keys={barMetrics}
              colors={COLORS}
              hidden={barToggle.hidden}
              onToggle={barToggle.toggle}
              testIdPrefix={`chart-bar-${platformKey}`}
            />
            {hasPointsInRange ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={enrichedSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
                  <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatBig} width={50} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7" }} />
                  {barMetrics.map((k, idx) => (
                    <Bar key={k} dataKey={k}
                         fill={COLORS[idx % COLORS.length]}
                         radius={[2, 2, 0, 0]}
                         hide={barToggle.isHidden(k)} />
                  ))}
                  {compareYoY && barMetrics.map((k, idx) => (
                    <Bar key={`${k}_prev`} dataKey={`${k}_prev`}
                         name={`${k} (prev yr)`}
                         fill={COLORS[idx % COLORS.length]} fillOpacity={0.35}
                         radius={[2, 2, 0, 0]}
                         hide={barToggle.isHidden(k)} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState msg="No data in this range." />
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function TrendBadge({ dir }) {
  if (dir === "up") {
    return (
      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
        ▲ UP
      </span>
    );
  }
  if (dir === "down") {
    return (
      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200">
        ▼ DOWN
      </span>
    );
  }
  return (
    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500 border border-zinc-200">
      ● FLAT
    </span>
  );
}

function EmptyChartState({ msg }) {
  return (
    <div className="h-[280px] grid place-items-center text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-sm">
      {msg}
    </div>
  );
}

function Header({ platformCount, monthCount }) {
  return (
    <div className="flex items-end justify-between gap-3 flex-wrap" data-testid="charts-header">
      <div>
        <div className="label-overline">Visualize</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Charts &amp; History</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          Per-platform trends and KPI tiles across every month uploaded. Pick a date range
          to scope the view, and tap any legend chip to hide / show a series.
        </p>
      </div>
      {platformCount > 0 && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-white border border-zinc-200 text-[11px] font-mono uppercase tracking-wider text-zinc-700">
            <Database className="h-3 w-3 text-zinc-400" />
            {platformCount} platform{platformCount === 1 ? "" : "s"}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-white border border-zinc-200 text-[11px] font-mono uppercase tracking-wider text-zinc-700">
            {monthCount} mo on file
          </span>
        </div>
      )}
    </div>
  );
}
